import type { ReactNode } from 'react';
import { Close, Modal } from '../design-system';
import { useTermsUrl } from '../footerLinks';

/** Trailing "Terms of Service" anchor of the disclaimer copy. A component
 *  (not a literal) so the href tracks the operator's `type: 'terms'` footer
 *  link, falling back to the platform default. With neither configured the
 *  anchor keeps its look but goes inert: no href (placeholder anchors aren't
 *  focusable) + pointer-events off, so a click can't navigate the iframe
 *  anywhere. */
function TermsOfServiceAnchor() {
  const termsUrl = useTermsUrl();
  return (
    <a
      {...(termsUrl
        ? {
            href: termsUrl,
            target: '_blank',
            rel: 'noopener noreferrer',
          }
        : { 'aria-disabled': true })}
      className={`underline ${termsUrl ? '' : 'pointer-events-none'}`}
      style={{ color: 'var(--co-DisclaimerModal-linkColor, var(--ui-accent))' }}
    >
      Terms of Service
    </a>
  );
}

/** Fixed legal copy shown when a user taps the pack "Disclaimer" link —
 *  Figma `706:1668`. ReactNode so the trailing "Terms of Service" is a real
 *  anchor; the URL resolves per operator at render time (see
 *  {@link TermsOfServiceAnchor}). */
export const BOX_WITHDRAWAL_DISCLAIMER: ReactNode = (
  <>
    Due to the high volume of gameplay and frequency of pack rips by players,
    items displayed or opened (the pulls) may occasionally become unavailable
    before fulfillment can be completed. In the rare event this occurs, you
    will be provided refund of Shipping &amp; Handling charges, plus a credit
    to your wallet for either (a) the FMV of the pulled item or (b) the pack
    price, whichever is greater. For more, see our <TermsOfServiceAnchor />.
  </>
);

const TITLE_ID = 'disclaimer-modal-title';

export interface DisclaimerModalProps {
  /** Backdrop / X / Dismiss close. */
  onClose: () => void;
  /** Heading — defaults to "Disclaimer". */
  title?: string;
  /** Body copy — defaults to {@link BOX_WITHDRAWAL_DISCLAIMER}. ReactNode
   *  so callers can embed links, accents, etc. */
  body?: ReactNode;
}

/**
 * Disclaimer modal — Figma `706:1668`. A light sheet (title + body + a single
 * outlined "Dismiss" button) over the shared design-system {@link Modal}
 * frame (backdrop dim/blur themed via `--co-Modal-*`). The sheet, text and
 * button are themed under the `DisclaimerModal` Design Constructor slot.
 */
export function DisclaimerModal({
  onClose,
  title = 'Disclaimer',
  body = BOX_WITHDRAWAL_DISCLAIMER,
}: DisclaimerModalProps) {
  return (
    <Modal
      onClose={onClose}
      inspectName="DisclaimerModal"
      ariaLabelledBy={TITLE_ID}
      widthClassName="max-w-[463px]"
      closeSlot={
        /* Close (X) — fixed top-right on phones, overlapping the sheet corner
           on ≥640px. translate utilities only (no inline transform) to dodge
           the Tailwind 4 transform-merge quirk.
           The phone `top` carries the host safe area: this button is fixed to
           the viewport OUTSIDE the modal frame, so nothing else insets it and
           a notch would make it untappable. It goes through `closeSlot` rather
           than into the card so the card's entrance transform never becomes
           its containing block. */
        <button
          type="button"
          onClick={onClose}
          aria-label="Close"
          className="
            fixed right-4 top-[calc(1rem+var(--page-safe-top))] z-[60]
            inline-flex h-10 w-10 items-center justify-center rounded-full
            transition-opacity hover:opacity-90 active:scale-95
            sm:absolute sm:right-0 sm:top-0 sm:z-10
            sm:-translate-y-1/2 sm:translate-x-full
          "
          style={{
            backgroundColor: 'var(--co-DisclaimerModal-closeFill, rgba(0,0,0,0.6))',
            color: 'var(--co-DisclaimerModal-closeIconColor, #FFFFFF)',
          }}
        >
          <Close size={18} strokeWidth={2} />
        </button>
      }
    >
      {/* Light sheet. */}
      <section
        data-co-component="DisclaimerModal"
        className="flex flex-col gap-6 rounded-[28px] p-5"
        style={{
          backgroundColor: 'var(--co-DisclaimerModal-cardFill, #FFFFFF)',
          fontFamily: 'var(--co-DisclaimerModal-fontFamily), Inter, system-ui, sans-serif',
        }}
      >
        <div className="flex flex-col gap-3">
          <h2
            id={TITLE_ID}
            className="font-semibold"
            style={{
              color: 'var(--co-DisclaimerModal-titleColor, #15181E)',
              fontSize: '20px',
              letterSpacing: '-0.4px',
              lineHeight: 1.3,
            }}
          >
            {title}
          </h2>
          <p
            style={{
              color: 'var(--co-DisclaimerModal-bodyColor, #15181E)',
              fontSize: '15px',
              lineHeight: 1.45,
            }}
          >
            {body}
          </p>
        </div>

        {/* Single outlined "Dismiss" CTA. */}
        <button
          type="button"
          onClick={onClose}
          className="flex h-[52px] w-full items-center justify-center rounded-full font-semibold transition-opacity hover:opacity-90 active:scale-[0.99]"
          style={{
            backgroundColor: 'var(--co-DisclaimerModal-dismissFill, #FFFFFF)',
            border: '1px solid var(--co-DisclaimerModal-dismissBorder, #DEDEE0)',
            color: 'var(--co-DisclaimerModal-dismissText, #15181E)',
            fontSize: '17px',
            letterSpacing: '-0.35px',
          }}
        >
          OK
        </button>
      </section>
    </Modal>
  );
}
